import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiGrid, FiUsers, FiBookOpen, FiCalendar, FiMessageSquare, 
  FiBarChart2, FiSettings, FiDollarSign, FiBell, FiZap,
  FiChevronRight, FiCheckSquare, FiFolder, FiCpu, FiMonitor,
  FiTrendingUp, FiSmartphone
} = FiIcons;

const NavItem = ({ to, icon, label, collapsed }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-300 group relative ${
        isActive 
          ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' 
          : 'text-slate-400 hover:text-white hover:bg-white/5'
      }`
    }
  >
    <SafeIcon icon={icon} className="text-xl min-w-[24px]" />
    <AnimatePresence>
      {!collapsed && (
        <motion.span
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          className="font-bold text-[10px] uppercase tracking-[0.2em] whitespace-nowrap"
        >
          {label}
        </motion.span>
      )}
    </AnimatePresence>
    {collapsed && (
      <div className="absolute left-full ml-4 px-3 py-2 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap border border-white/10">
        {label}
      </div>
    )}
  </NavLink>
);

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(true);

  const menuItems = [
    { to: '/', icon: FiGrid, label: 'Dashboard' },
    { to: '/crm', icon: FiUsers, label: 'Clients' },
    { to: '/courses', icon: FiBookOpen, label: 'Courses' },
    { to: '/sessions', icon: FiCalendar, label: 'Sessions' },
    { to: '/messages', icon: FiMessageSquare, label: 'Messages' },
    { to: '/assessments', icon: FiCheckSquare, label: 'Assessments' },
    { to: '/resources', icon: FiFolder, label: 'Vault' },
  ];

  const powerTools = [
    { to: '/automations', icon: FiZap, label: 'CoachFlow' },
    { to: '/growth-engine', icon: FiTrendingUp, label: 'Growth' },
    { to: '/ai-architect', icon: FiCpu, label: 'AI Architect' },
    { to: '/analytics', icon: FiBarChart2, label: 'Analytics' },
    { to: '/monetization', icon: FiDollarSign, label: 'Revenue' },
    { to: '/notifications', icon: FiBell, label: 'Alerts' },
    { to: '/student-dashboard', icon: FiSmartphone, label: 'Student UI' },
  ];

  return (
    <motion.aside
      onHoverStart={() => setCollapsed(false)}
      onHoverEnd={() => setCollapsed(true)}
      animate={{ width: collapsed ? '88px' : '280px' }}
      className="h-screen sticky top-0 bg-[#0a0a0a] border-r border-white/5 flex flex-col z-50"
    >
      {/* Logo */}
      <div className="p-6 flex items-center gap-4">
        <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shrink-0 shadow-lg shadow-indigo-600/20">
          <span className="text-white font-black text-xl italic tracking-tighter">S</span>
        </div>
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-white font-black text-lg tracking-tighter uppercase italic"
          >
            Session<span className="text-indigo-500">HQ</span>
          </motion.span>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-8 scrollbar-hide">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <NavItem key={item.to} {...item} collapsed={collapsed} />
          ))}
        </div>

        <div>
          {!collapsed && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="px-4 mb-4 text-[9px] font-black text-slate-600 uppercase tracking-[0.3em]"
            >
              Power Tools
            </motion.p>
          )}
          <div className="space-y-1">
            {powerTools.map((item) => (
              <NavItem key={item.to} {...item} collapsed={collapsed} />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="p-3 border-t border-white/5 space-y-1">
        <NavItem to="/debug" icon={FiMonitor} label="System" collapsed={collapsed} />
        <NavItem to="/settings" icon={FiSettings} label="Settings" collapsed={collapsed} />
        
        <div className="mt-4 p-3 bg-white/5 rounded-2xl flex items-center gap-3 overflow-hidden">
          <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 shrink-0" />
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="overflow-hidden"
            >
              <p className="text-[10px] font-black text-white uppercase truncate">Coach Alex</p>
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Pro Plan</p>
            </motion.div>
          )}
        </div>
      </div>
    </motion.aside>
  );
}